/**
 * The helpdesk's shape, as staff manage it.
 *
 * Shows what readers cannot: hidden sections, pages with no text yet, archived
 * ones. That is the point — a section for a feature nobody has documented can
 * only be built out if the half-finished state is visible somewhere.
 *
 * Creating a page does not ask for its text. It makes the page, then opens the
 * editor on it, so writing happens in the one place that previews and validates.
 */

import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  IconArchive,
  IconArrowLeft,
  IconChevronDown,
  IconChevronUp,
  IconEye,
  IconEyeOff,
  IconFilePlus,
  IconFolderPlus,
  IconHistory,
  IconPencil,
  IconPlus,
  IconRestore,
  IconSettings,
  IconThumbDown,
} from "@tabler/icons-react"
import { useMemo, useState } from "react"
import { Link } from "react-router"
import {
  helpStructureRequests,
  useHelpAdminTree,
  useHelpStructureMutation,
} from "../api/helpAdminApi"
import type {
  HelpAdminArticle,
  HelpAdminCategory,
  HelpAdminGroup,
  HelpAdminTree,
} from "../api/types"
import { editorLang, HELP_LANGS } from "../content"
import { HELP_BASE } from "../routes"
import { HelpdeskArticleEditor } from "./HelpdeskArticleEditor"
import { HelpdeskArticleHistory } from "./HelpdeskArticleHistory"
import {
  GroupDialog,
  NewPageDialog,
  SectionDialog,
} from "./HelpdeskStructureDialogs"

type Dialog =
  | { kind: "section"; existing?: HelpAdminCategory }
  | { kind: "group"; categoryId: string; existing?: HelpAdminGroup }
  | { kind: "page"; categoryId: string; groupId: string }
  | null

function StatusPill({ article }: { article: HelpAdminArticle }) {
  const { translate } = useTranslation()
  const tone =
    article.status === "published"
      ? "border-white/10 text-white/50"
      : article.status === "archived"
        ? "border-white/10 text-white/30"
        : "border-yellow1/30 text-yellow1"
  const label =
    article.status === "published"
      ? article.publishedLangs.join(", ").toUpperCase()
      : translate(
          article.status === "archived"
            ? "HELPDESK/MANAGE_ARCHIVED"
            : "HELPDESK/MANAGE_UNWRITTEN",
        )

  return (
    <span className={cn("rounded-full border px-2 py-0.5 text-[11px]", tone)}>
      {label}
    </span>
  )
}

function MoveButtons({
  index,
  count,
  name,
  disabled,
  onMove,
}: {
  index: number
  count: number
  /** Named, because a tree of identical "move up" buttons is unusable by voice or screen reader. */
  name: string
  /** Unavailable during a write, whose positions are mid-change, and while the
   * tree is filtered, where the visible siblings are not the whole scope a
   * reorder has to send. */
  disabled: boolean
  onMove: (delta: number) => void
}) {
  const { translate } = useTranslation()
  return (
    <span className="flex items-center">
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 text-grey5 hover:text-white"
        aria-label={translate("HELPDESK/MANAGE_MOVE_UP", { name })}
        disabled={disabled || index === 0}
        onClick={() => onMove(-1)}
      >
        <IconChevronUp size={14} />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 text-grey5 hover:text-white"
        aria-label={translate("HELPDESK/MANAGE_MOVE_DOWN", { name })}
        disabled={disabled || index === count - 1}
        onClick={() => onMove(1)}
      >
        <IconChevronDown size={14} />
      </Button>
    </span>
  )
}

/** A new order with the item at `index` moved by `delta`. */
function moved(ids: string[], index: number, delta: number): string[] {
  const next = [...ids]
  const [item] = next.splice(index, 1)
  next.splice(index + delta, 0, item)
  return next
}

export function HelpdeskManage({ lang }: { lang: string }) {
  const { translate } = useTranslation()
  const [dialog, setDialog] = useState<Dialog>(null)
  const [editing, setEditing] = useState<HelpAdminArticle | null>(null)
  const [editingLang, setEditingLang] = useState(lang)
  const [historyOf, setHistoryOf] = useState<HelpAdminArticle | null>(null)
  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false)

  const openEditor = (article: HelpAdminArticle) => {
    setEditingLang(editorLang(lang, article))
    setEditing(article)
  }

  const openHistory = (article: HelpAdminArticle) => {
    setEditingLang(editorLang(lang, article))
    setHistoryOf(article)
  }

  const tree = useHelpAdminTree(lang, true)
  const mutate = useHelpStructureMutation(lang)

  const needsReviewCount = useMemo(
    () =>
      (tree.data?.categories ?? []).reduce(
        (total, category) =>
          total +
          category.groups.reduce(
            (groupTotal, group) =>
              groupTotal + group.articles.filter((a) => a.needsReview).length,
            0,
          ),
        0,
      ),
    [tree.data],
  )

  // Filtering keeps the tree's shape rather than flattening to a list: a page is
  // easier to place, and to fix, where it actually sits.
  const categories = useMemo(() => {
    const all = tree.data?.categories ?? []
    if (!onlyNeedsReview) return all
    return all
      .map((category) => ({
        ...category,
        groups: category.groups
          .map((group) => ({
            ...group,
            articles: group.articles.filter((a) => a.needsReview),
          }))
          .filter((group) => group.articles.length > 0),
      }))
      .filter((category) => category.groups.length > 0)
  }, [tree.data, onlyNeedsReview])

  const run = async (request: () => Promise<{ data: unknown }>) => {
    try {
      await mutate.mutateAsync(request)
    } catch {
      // 5xx toasts itself; a 4xx here is a refusal the dialogs surface.
      toast.error(translate("HELPDESK/MANAGE_FAILED"))
    }
  }

  if (historyOf) {
    return (
      <HelpdeskArticleHistory
        key={`${historyOf.id}-${editingLang}`}
        articleId={historyOf.id}
        lang={editingLang}
        onClose={() => setHistoryOf(null)}
      />
    )
  }

  if (editing) {
    return (
      <HelpdeskArticleEditor
        // Remounted per language: each is its own text with its own versions.
        key={`${editing.id}-${editingLang}`}
        articleId={editing.id}
        articleTitle={editing.title}
        lang={editingLang}
        languages={HELP_LANGS}
        onLangChange={setEditingLang}
        onClose={() => {
          setEditing(null)
          void tree.refetch()
        }}
      />
    )
  }

  return (
    <div className="h-full min-w-0 overflow-y-auto bg-grey1 px-M py-L lg:px-XXL lg:py-XL">
      <div className="mx-auto max-w-5xl">
        <Link
          to={HELP_BASE}
          className="mb-M flex w-fit items-center gap-1.5 text-grey5 hover:text-white"
        >
          <IconArrowLeft size={14} />
          <Typography as="span" variant="textXS">
            {translate("HELPDESK/TITLE")}
          </Typography>
        </Link>

        <div className="flex flex-wrap items-center justify-between gap-M pb-S">
          <Typography as="h1" variant="titleL">
            {translate("HELPDESK/MANAGE_TITLE")}
          </Typography>
          <Button size="sm" onClick={() => setDialog({ kind: "section" })}>
            <IconPlus size={14} className="mr-1.5" />
            {translate("HELPDESK/MANAGE_NEW_SECTION")}
          </Button>
        </div>
        <Typography as="p" variant="textM" className="pb-M text-grey5">
          {translate("HELPDESK/MANAGE_SUBTITLE")}
        </Typography>

        {/* The automation flags articles whose code has moved on; without this the
          flag it writes is invisible and the loop never closes. */}
        {needsReviewCount > 0 ? (
          <div className="mb-L flex flex-wrap items-center gap-M rounded-lg border border-yellow1/25 bg-yellow1/6 px-M py-3">
            <Typography as="span" variant="textXS" className="text-white/80">
              {translate("HELPDESK/MANAGE_REVIEW_WAITING", {
                count: needsReviewCount,
              })}
            </Typography>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              aria-pressed={onlyNeedsReview}
              onClick={() => setOnlyNeedsReview((only) => !only)}
            >
              {translate(
                onlyNeedsReview
                  ? "HELPDESK/MANAGE_REVIEW_SHOW_ALL"
                  : "HELPDESK/MANAGE_REVIEW_ONLY",
              )}
            </Button>
          </div>
        ) : null}

        {tree.isLoading ? (
          <div className="flex justify-center py-XL">
            <LoadingSpinner />
          </div>
        ) : tree.isError || !tree.data ? (
          <Typography as="p" variant="textS" className="text-white/60">
            {translate("HELPDESK/MANAGE_LOAD_ERROR")}
          </Typography>
        ) : categories.length === 0 ? (
          <Typography as="p" variant="textS" className="text-white/60">
            {translate(
              onlyNeedsReview
                ? "HELPDESK/MANAGE_REVIEW_NONE"
                : "HELPDESK/MANAGE_EMPTY",
            )}
          </Typography>
        ) : (
          <div className="flex flex-col gap-M">
            {categories.map((category, index) => (
              <SectionCard
                key={category.id}
                category={category}
                index={index}
                siblings={tree.data as HelpAdminTree}
                busy={mutate.isPending}
                onRun={run}
                onNewGroup={() =>
                  setDialog({ kind: "group", categoryId: category.id })
                }
                onNewPage={(groupId) =>
                  setDialog({ kind: "page", categoryId: category.id, groupId })
                }
                onEditSection={() =>
                  setDialog({ kind: "section", existing: category })
                }
                onEditGroup={(group) =>
                  setDialog({
                    kind: "group",
                    categoryId: category.id,
                    existing: group,
                  })
                }
                onEdit={openEditor}
                onHistory={openHistory}
                filtered={onlyNeedsReview}
              />
            ))}
          </div>
        )}
      </div>

      {dialog?.kind === "section" ? (
        <SectionDialog
          lang={lang}
          visualKeys={tree.data?.visualKeys ?? []}
          existing={dialog.existing}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "group" ? (
        <GroupDialog
          lang={lang}
          categoryId={dialog.categoryId}
          existing={dialog.existing}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "page" ? (
        <NewPageDialog
          lang={lang}
          categoryId={dialog.categoryId}
          groupId={dialog.groupId}
          onClose={() => setDialog(null)}
          onCreated={(article) => {
            setDialog(null)
            // Through openEditor, not setEditing: a new page is German-authoritative
            // and has no text in any language, so the language has to be decided by
            // the same rule as every other page rather than left on whatever the
            // last edit used.
            openEditor(article)
          }}
        />
      ) : null}
    </div>
  )
}

function SectionCard({
  category,
  index,
  siblings,
  busy,
  onRun,
  onNewGroup,
  onNewPage,
  onEditSection,
  onEditGroup,
  onEdit,
  onHistory,
  filtered,
}: {
  category: HelpAdminCategory
  index: number
  siblings: HelpAdminTree
  busy: boolean
  onRun: (request: () => Promise<{ data: unknown }>) => void
  onNewGroup: () => void
  onNewPage: (groupId: string) => void
  onEditSection: () => void
  onEditGroup: (group: HelpAdminGroup) => void
  onEdit: (article: HelpAdminArticle) => void
  onHistory: (article: HelpAdminArticle) => void
  filtered: boolean
}) {
  const { translate } = useTranslation()
  const categoryIds = siblings.categories.map((c) => c.id)

  return (
    <section className="rounded-xl border border-white/6 bg-grey2/40">
      <header className="flex flex-wrap items-center gap-2 border-b border-white/6 px-M py-3">
        <Typography as="h2" variant="textM-bold">
          {category.title || category.id}
        </Typography>
        <span className="font-mono text-[11px] text-white/30">
          {category.id}
        </span>
        {category.isPublished ? null : (
          <span className="rounded-full border border-yellow1/30 px-2 py-0.5 text-[11px] text-yellow1">
            {translate("HELPDESK/MANAGE_HIDDEN")}
          </span>
        )}
        {category.requiredFlags.map((flag) => (
          <span
            key={flag}
            className="font-mono rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-white/40"
          >
            {flag}
          </span>
        ))}

        <span className="ml-auto flex items-center gap-1">
          <MoveButtons
            index={index}
            count={categoryIds.length}
            name={category.title || category.id}
            disabled={busy || filtered}
            onMove={(delta) =>
              onRun(
                helpStructureRequests.reorder(
                  "category",
                  null,
                  moved(categoryIds, index, delta),
                ),
              )
            }
          />
          <Button
            size="sm"
            variant="ghost"
            className="text-grey5 hover:text-white"
            disabled={busy}
            onClick={() =>
              onRun(
                helpStructureRequests.patchCategory(category.id, {
                  isPublished: !category.isPublished,
                }),
              )
            }
          >
            {category.isPublished ? (
              <IconEyeOff size={14} className="mr-1.5" />
            ) : (
              <IconEye size={14} className="mr-1.5" />
            )}
            {translate(
              category.isPublished
                ? "HELPDESK/MANAGE_HIDE"
                : "HELPDESK/MANAGE_PUBLISH",
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-grey5 hover:text-white"
            onClick={onEditSection}
          >
            <IconSettings size={14} className="mr-1.5" />
            {translate("HELPDESK/MANAGE_EDIT_SECTION")}
          </Button>
          <Button size="sm" variant="outline" onClick={onNewGroup}>
            <IconFolderPlus size={14} className="mr-1.5" />
            {translate("HELPDESK/MANAGE_NEW_GROUP")}
          </Button>
        </span>
      </header>

      {category.groups.length === 0 ? (
        <Typography as="p" variant="textXS" className="px-M py-3 text-white/40">
          {translate("HELPDESK/MANAGE_NO_GROUPS")}
        </Typography>
      ) : (
        <div className="flex flex-col">
          {category.groups.map((group, groupIndex) => (
            <GroupRow
              key={group.id}
              group={group}
              index={groupIndex}
              siblingIds={category.groups.map((g) => g.id)}
              categoryId={category.id}
              busy={busy}
              onRun={onRun}
              onNewPage={() => onNewPage(group.id)}
              onEditGroup={() => onEditGroup(group)}
              onEdit={onEdit}
              onHistory={onHistory}
              filtered={filtered}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function GroupRow({
  group,
  index,
  siblingIds,
  categoryId,
  busy,
  onRun,
  onNewPage,
  onEditGroup,
  onEdit,
  onHistory,
  filtered,
}: {
  group: HelpAdminGroup
  index: number
  siblingIds: string[]
  categoryId: string
  busy: boolean
  onRun: (request: () => Promise<{ data: unknown }>) => void
  onNewPage: () => void
  onEditGroup: () => void
  onEdit: (article: HelpAdminArticle) => void
  onHistory: (article: HelpAdminArticle) => void
  filtered: boolean
}) {
  const { translate } = useTranslation()
  const articleIds = group.articles.map((a) => a.id)

  return (
    <div className="border-b border-white/4 px-M py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <Typography as="h3" variant="textS-bold" className="text-white/80">
          {group.title || group.id}
        </Typography>
        <span className="ml-auto flex items-center gap-1">
          <MoveButtons
            index={index}
            count={siblingIds.length}
            name={group.title || group.id}
            disabled={busy || filtered}
            onMove={(delta) =>
              onRun(
                helpStructureRequests.reorder(
                  "group",
                  categoryId,
                  moved(siblingIds, index, delta),
                ),
              )
            }
          />
          <Button
            size="sm"
            variant="ghost"
            className="text-grey5 hover:text-white"
            aria-label={translate("HELPDESK/MANAGE_EDIT_GROUP")}
            onClick={onEditGroup}
          >
            <IconSettings size={14} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-grey5 hover:text-white"
            onClick={onNewPage}
          >
            <IconFilePlus size={14} className="mr-1.5" />
            {translate("HELPDESK/MANAGE_NEW_PAGE")}
          </Button>
        </span>
      </div>

      {group.articles.length === 0 ? (
        <Typography as="p" variant="textXS" className="pt-1 text-white/30">
          {translate("HELPDESK/MANAGE_NO_PAGES")}
        </Typography>
      ) : (
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {group.articles.map((article, articleIndex) => (
            <li
              key={article.id}
              className="flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 hover:bg-white/3"
            >
              <span className="font-mono text-[12px] text-white/70">
                {article.id}
              </span>
              <StatusPill article={article} />
              {article.draftLangs.length > 0 ? (
                <span className="rounded-full bg-yellow1/15 px-2 py-0.5 text-[11px] text-yellow1">
                  {translate("HELPDESK/MANAGE_HAS_DRAFT", {
                    langs: article.draftLangs.join(", ").toUpperCase(),
                  })}
                </span>
              ) : null}
              {article.needsReview ? (
                <span className="rounded-full border border-yellow1/30 px-2 py-0.5 text-[11px] text-yellow1">
                  {translate("HELPDESK/MANAGE_NEEDS_REVIEW")}
                </span>
              ) : null}
              {article.notHelpfulCount > 0 ? (
                <span
                  className="flex items-center gap-1 text-[11px] text-white/35"
                  title={translate("HELPDESK/MANAGE_VOTES", {
                    up: article.helpfulCount,
                    down: article.notHelpfulCount,
                  })}
                >
                  <IconThumbDown size={11} />
                  {article.notHelpfulCount}
                </span>
              ) : null}

              <span className="ml-auto flex items-center gap-1">
                <MoveButtons
                  index={articleIndex}
                  count={articleIds.length}
                  name={article.id}
                  disabled={busy || filtered}
                  onMove={(delta) =>
                    onRun(
                      helpStructureRequests.reorder(
                        "article",
                        group.id,
                        moved(articleIds, articleIndex, delta),
                      ),
                    )
                  }
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-grey5 hover:text-white"
                  disabled={busy}
                  onClick={() =>
                    onRun(
                      helpStructureRequests.patchArticle(article.id, {
                        status:
                          article.status === "archived"
                            ? "published"
                            : "archived",
                      }),
                    )
                  }
                >
                  {article.status === "archived" ? (
                    <IconRestore size={14} />
                  ) : (
                    <IconArchive size={14} />
                  )}
                  <span className="sr-only">
                    {translate(
                      article.status === "archived"
                        ? "HELPDESK/MANAGE_RESTORE"
                        : "HELPDESK/MANAGE_ARCHIVE",
                    )}
                  </span>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-grey5 hover:text-white"
                  aria-label={translate("HELPDESK/HISTORY_TITLE")}
                  onClick={() => onHistory(article)}
                >
                  <IconHistory size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onEdit(article)}
                >
                  <IconPencil size={14} className="mr-1.5" />
                  {translate("HELPDESK/EDIT")}
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
